import { Delete, PictureAsPdf } from '@mui/icons-material';
import { Box, Card, CardMedia, CircularProgress, IconButton, Typography } from '@mui/material';
import { isPdfFile } from '../../../../utils/pdf/detector';
import type { PhotoItem } from '../../hooks/use-photo-manager';
import { ConvertingOverlay } from './converting-overlay';

interface PhotoCardProps {
  item: PhotoItem;
  onRemove: (id: string) => void;
}

export function PhotoCard({ item, onRemove }: PhotoCardProps) {
  const hasPreview = item.previewUrls.length > 0;
  const isMultiPage = item.previewUrls.length > 1;

  return (
    <Card sx={{ position: 'relative' }}>
      {hasPreview ? (
        item.previewUrls.map((url, idx) => (
          <Box key={url} sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              image={url}
              alt={`${item.file.name} - ${idx + 1}`}
              sx={{ height: 150, objectFit: 'cover' }}
            />
            {isMultiPage && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'white',
                  px: 0.75,
                  borderRadius: 0.5,
                  lineHeight: 1.6,
                }}
              >
                {idx + 1} / {item.previewUrls.length}
              </Typography>
            )}
          </Box>
        ))
      ) : isPdfFile(item.file) ? (
        <Box
          sx={{
            height: 150,
            bgcolor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography variant="caption" noWrap sx={{ maxWidth: '90%', textAlign: 'center' }}>
            {item.file.name}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 150,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {item.isConverting && hasPreview && <ConvertingOverlay />}

      <IconButton
        size="small"
        onClick={() => onRemove(item.id)}
        disabled={item.isConverting}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'error.main',
          color: 'white',
          '&:hover': { bgcolor: 'error.dark' },
          '&.Mui-disabled': {
            bgcolor: 'action.disabled',
            color: 'text.disabled',
          },
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Card>
  );
}
